import { useEffect, useState } from 'react';
import { useLang } from './i18n';

// GA4 Measurement ID. Public by design (ships in the page), so it's safe to
// hardcode a default; an env var can still override per environment.
const GA_ID = process.env.REACT_APP_GA_ID || 'G-QL045K6M9T';
const CONSENT_KEY = 'mynstur-cookie-consent';

// Inject gtag.js once and send the initial pageview.
function loadGA() {
  if (!GA_ID || window.__gaLoaded) return;
  window.__gaLoaded = true;

  const s = document.createElement('script');
  s.async = true;
  s.src = `https://www.googletagmanager.com/gtag/js?id=${GA_ID}`;
  document.head.appendChild(s);

  window.dataLayer = window.dataLayer || [];
  function gtag() {
    window.dataLayer.push(arguments);
  }
  window.gtag = gtag;
  gtag('js', new Date());
  gtag('config', GA_ID);
}

// Tell the Clero pixel about the consent decision (its bootstrap lives in
// index.html; it queues 'consent' until pixel.js loads).
function setCleroConsent(value) {
  try {
    if (typeof window.clero === 'function') window.clero('consent', value);
  } catch {
    /* ignore */
  }
}

// Apply a consent decision to every tracker.
function apply(value) {
  if (value === 'granted') {
    loadGA();
    setCleroConsent('granted');
  } else {
    setCleroConsent('denied');
  }
}

// Consent-gated analytics: shows a cookie banner; GA + Clero only run on accept.
export default function Analytics() {
  const { t } = useLang();
  const [choice, setChoice] = useState(() => {
    try {
      return localStorage.getItem(CONSENT_KEY);
    } catch {
      return null;
    }
  });
  const [leaving, setLeaving] = useState(false);

  // Re-apply a stored decision on load (so returning visitors keep their choice).
  useEffect(() => {
    if (choice === 'granted' || choice === 'denied') apply(choice);
  }, [choice]);

  const decide = (value) => {
    try {
      localStorage.setItem(CONSENT_KEY, value);
    } catch {
      /* ignore storage errors */
    }
    apply(value);
    // Play the exit animation, then unmount.
    setLeaving(true);
    window.setTimeout(() => setChoice(value), 420);
  };

  // No banner once a choice has been made.
  if (choice) return null;

  return (
    <div
      className={`consent ${leaving ? 'consent--leaving' : ''}`}
      role="dialog"
      aria-label="Cookie consent"
    >
      <p className="consent__text">{t.consent.text}</p>
      <div className="consent__actions">
        <button className="consent__btn consent__btn--ghost" onClick={() => decide('denied')}>
          {t.consent.decline}
        </button>
        <button className="consent__btn consent__btn--accept" onClick={() => decide('granted')}>
          {t.consent.accept}
        </button>
      </div>
    </div>
  );
}
